// Forgot password — requests a reset email.

import { useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { useDict } from "@/i18n/LocaleProvider";
import { api, ApiError } from "@/lib/api";

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail);
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const t = useDict().app.auth;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await api("/auth/forgot-password", {
        method: "POST",
        body: { email },
      });
      setDone(true);
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center p-5">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="TrueHacking" className="h-20 w-auto mb-4 mx-auto" />
          <div className="font-display text-[32px] font-bold text-accent tracking-[0.14em]">
            TRUEHACKING
          </div>
          <div className="font-mono text-[10px] text-dim tracking-[0.1em] mt-1 uppercase">
            {t.recoverySubtitle}
          </div>
        </div>

        <Card className="!p-5 sm:!p-7">
          {done ? (
            <>
              <div className="font-display text-[18px] font-semibold text-content mb-2">
                {t.checkEmail}
              </div>
              <div className="font-mono text-[11px] text-muted leading-[1.6] mb-5">
                {t.forgotSentPre}{" "}<span className="text-accent">{email}</span>{t.forgotSentPost}
              </div>
              <Link
                to="/login"
                className="text-accent font-mono text-[11px] underline"
              >
                ← Back to sign in
              </Link>
            </>
          ) : (
            <>
              <div className="font-display text-[18px] font-semibold text-content mb-1">
                {t.forgotHeading}
              </div>
              <div className="font-mono text-[11px] text-muted mb-5">
                {t.forgotSub}
              </div>

              <div
                onKeyDown={(e) => {
                  if (e.key === "Enter" && email && !busy) submit();
                }}
              >
                <Input
                  label={t.email}
                  value={email}
                  onChange={setEmail}
                  placeholder={t.emailPlaceholder}
                />
              </div>

              {err && (
                <div className="px-3 py-2 bg-danger-bg border border-danger/40 rounded text-danger font-mono text-[11px] mb-3">
                  {err}
                </div>
              )}

              <Button
                variant="primary"
                full
                onClick={submit}
                disabled={busy || !email}
              >
                {busy ? t.busy : t.sendReset}
              </Button>

              <div className="mt-4 text-center">
                <Link
                  to="/login"
                  className="text-accent font-mono text-[11px] underline"
                >
                  ← {t.backToSignin}
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
